import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PerformanceTestResult, Athlete } from '../types';
import { performanceTests } from '../data/performanceTestData';
import { getPerformanceLevel, getPerformanceLevelLabel } from '../data/testThresholds';

interface ReportData {
  athleteId?: string;
  athleteName?: string;
  category: string;
  categoryLabel: string;
  testResults: PerformanceTestResult[];
  athletes: Athlete[];
}

// Export to Excel
export function exportToExcel(data: ReportData) {
  const { athleteId, athleteName, category, categoryLabel, testResults, athletes } = data;

  // Filter tests by category
  const categoryTests = performanceTests.filter(test => test.category === category);

  // Create workbook
  const wb = XLSX.utils.book_new();

  // Sheet 1: Data Entry
  const sheetData: any[] = [];

  // Header row
  const headerRow = ['Atleta'];
  categoryTests.forEach(test => {
    headerRow.push(`${test.name} (${test.unit})`);
  });
  sheetData.push(headerRow);

  // Filter athletes if single athlete
  const reportAthletes = athleteId
    ? athletes.filter(a => a.id === athleteId)
    : athletes;

  // Data rows
  reportAthletes.forEach(athlete => {
    const row: any[] = [athlete.name];
    categoryTests.forEach(test => {
      const results = testResults.filter(r =>
        r.athleteId === athlete.id && r.testId === test.id
      );

      if (results.length > 0) {
        // Get latest result
        const latest = results.sort((a, b) =>
          new Date(b.date).getTime() - new Date(a.date).getTime()
        )[0];
        row.push(latest.value);
      } else {
        row.push('');
      }
    });
    sheetData.push(row);
  });

  const ws1 = XLSX.utils.aoa_to_sheet(sheetData);

  // Set column widths
  ws1['!cols'] = [
    { wch: 20 }, // Athlete name
    ...categoryTests.map(() => ({ wch: 15 }))
  ];

  XLSX.utils.book_append_sheet(wb, ws1, 'Risultati');

  // Sheet 2: Historical Data
  const historicalData: any[] = [];
  historicalData.push(['Atleta', 'Test', 'Data', 'Valore', 'Unità', 'Livello']);

  reportAthletes.forEach(athlete => {
    categoryTests.forEach(test => {
      const results = testResults
        .filter(r => r.athleteId === athlete.id && r.testId === test.id)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      results.forEach(result => {
        const level = getPerformanceLevel(
          result.value,
          test.id,
          athlete.id,
          test.higherIsBetter
        );
        const levelLabel = getPerformanceLevelLabel(level);

        historicalData.push([
          athlete.name,
          test.name,
          new Date(result.date).toLocaleDateString('it-IT'),
          result.value,
          test.unit,
          levelLabel
        ]);
      });
    });
  });

  const ws2 = XLSX.utils.aoa_to_sheet(historicalData);
  ws2['!cols'] = [
    { wch: 20 },
    { wch: 25 },
    { wch: 12 },
    { wch: 10 },
    { wch: 10 },
    { wch: 15 }
  ];

  XLSX.utils.book_append_sheet(wb, ws2, 'Storico');

  // Sheet 3: Statistics
  const statsData: any[] = [];
  statsData.push(['Test', 'Media', 'Massimo', 'Minimo', 'Unità']);

  categoryTests.forEach(test => {
    const values = testResults
      .filter(r => r.testId === test.id)
      .map(r => r.value)
      .filter(v => v > 0);

    if (values.length > 0) {
      const avg = values.reduce((a, b) => a + b, 0) / values.length;
      const max = Math.max(...values);
      const min = Math.min(...values);

      statsData.push([
        test.name,
        avg.toFixed(2),
        max.toFixed(2),
        min.toFixed(2),
        test.unit
      ]);
    }
  });

  const ws3 = XLSX.utils.aoa_to_sheet(statsData);
  ws3['!cols'] = [
    { wch: 25 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 10 }
  ];

  XLSX.utils.book_append_sheet(wb, ws3, 'Statistiche');

  // Generate filename
  const fileName = athleteId
    ? `report_${athleteName}_${categoryLabel}_${new Date().toISOString().split('T')[0]}.xlsx`
    : `report_team_${categoryLabel}_${new Date().toISOString().split('T')[0]}.xlsx`;

  // Download
  XLSX.writeFile(wb, fileName);
}

// Export to PDF
export function exportToPDF(data: ReportData) {
  const { athleteId, athleteName, category, categoryLabel, testResults, athletes } = data;

  const doc = new jsPDF();

  // Filter tests by category
  const categoryTests = performanceTests.filter(test => test.category === category);

  // Filter athletes if single athlete
  const reportAthletes = athleteId
    ? athletes.filter(a => a.id === athleteId)
    : athletes;

  // Header
  doc.setFillColor(0, 33, 44); // #00212c
  doc.rect(0, 0, 210, 35, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.text('Report Performance Testing', 15, 15);

  doc.setFontSize(12);
  doc.text(`Categoria: ${categoryLabel}`, 15, 25);

  if (athleteId && athleteName) {
    doc.text(`Atleta: ${athleteName}`, 15, 32);
  } else {
    doc.text('Team Report', 15, 32);
  }

  // Date
  doc.setFontSize(10);
  doc.text(`Data: ${new Date().toLocaleDateString('it-IT')}`, 150, 25);

  let yPosition = 45;

  // Table 1: Latest Results
  doc.setTextColor(0, 33, 44);
  doc.setFontSize(14);
  doc.text('Risultati Recenti', 15, yPosition);
  yPosition += 5;

  const tableData: any[] = [];
  reportAthletes.forEach(athlete => {
    const row: any[] = [athlete.name];
    categoryTests.forEach(test => {
      const results = testResults.filter(r =>
        r.athleteId === athlete.id && r.testId === test.id
      );

      if (results.length > 0) {
        const latest = results.sort((a, b) =>
          new Date(b.date).getTime() - new Date(a.date).getTime()
        )[0];

        row.push(`${latest.value} ${test.unit}`);
      } else {
        row.push('-');
      }
    });
    tableData.push(row);
  });

  autoTable(doc, {
    startY: yPosition,
    head: [['Atleta', ...categoryTests.map(t => `${t.name}\n(${t.unit})`)]],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [0, 33, 44],
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 9
    },
    alternateRowStyles: {
      fillColor: [246, 246, 246]
    },
    margin: { left: 15, right: 15 }
  });

  // @ts-ignore - autoTable extends jsPDF
  yPosition = doc.lastAutoTable.finalY + 15;

  // Add new page if needed
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  // Table 2: Statistics
  doc.setFontSize(14);
  doc.text('Statistiche', 15, yPosition);
  yPosition += 5;

  const statsData: any[] = [];
  categoryTests.forEach(test => {
    const values = testResults
      .filter(r => r.testId === test.id)
      .map(r => r.value)
      .filter(v => v > 0);

    if (values.length > 0) {
      const avg = values.reduce((a, b) => a + b, 0) / values.length;
      const max = Math.max(...values);
      const min = Math.min(...values);

      statsData.push([
        test.name,
        `${avg.toFixed(2)} ${test.unit}`,
        `${max.toFixed(2)} ${test.unit}`,
        `${min.toFixed(2)} ${test.unit}`
      ]);
    }
  });

  autoTable(doc, {
    startY: yPosition,
    head: [['Test', 'Media', 'Massimo', 'Minimo']],
    body: statsData,
    theme: 'grid',
    headStyles: {
      fillColor: [0, 33, 44],
      textColor: [255, 255, 255],
      fontSize: 10,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 9
    },
    alternateRowStyles: {
      fillColor: [246, 246, 246]
    },
    margin: { left: 15, right: 15 }
  });

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(77, 77, 77);
    doc.text(
      `Pagina ${i} di ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }

  // Generate filename
  const fileName = athleteId
    ? `report_${athleteName}_${categoryLabel}_${new Date().toISOString().split('T')[0]}.pdf`
    : `report_team_${categoryLabel}_${new Date().toISOString().split('T')[0]}.pdf`;

  // Download
  doc.save(fileName);
}
